/* istanbul ignore file */
/**
 * Created by gboue on 08/22/22
 * File: Providers
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import {
  Button,
  Title,
  useModal
} from '@panwds/react-ui'
import {
  DESC_HAS_NO_FEDERATIONS,
  HEADING_HAS_NO_FEDERATIONS,
  LABEL_DELETE_OWNER_DNS_TEXT,
  LABEL_PROVIDERS_TITLE, SUCCESS_GENERIC,
} from '../../../constants/LabelConstants'

import ZIndex from '../../../../common/components/zIndex/ZIndex'
import NoDataScreen from '../../../../common/components/noDataScreen/NoDataScreen'
import DomainModal from '../modal/DomainModal'
import { BUTTON_ADD_IDENTITY_FEDERATION } from '../../../../common/constants/LabelConstants'
import ProviderCard from '../cards/ProviderCard'
import { ARRAY } from '../../../../common/utils/Utils'
import GlobalContext from '../../../../common/context/GlobalContext'
import { useMessage } from '../../../../common/providers/MessageProvider'
import ScreenLoader from '../../../../common/components/screenLoader/ScreenLoader'
import AuthContext from '../../../../common/context/AuthContext'
import { LoadFederations } from '../../../actions/IAMActions'
import IdentityService from '../../../services/IdentityService'
import { IDP_OWNER_STATUS } from '../../../constants/IAMConstants'
import {
  setFederationMappings,
} from '../../../../common/reducers/actions/IAMReducer.actions'
import { getVarValue } from '../../../../common/utils/Sparky'
import { FF_ENABLE_FEDERATION_MAPPING } from '../../../../common/utils/FeatureFlags'

const federationMappingsEnabled = getVarValue(FF_ENABLE_FEDERATION_MAPPING)

/**
  *   Identity providers page
  */
const Providers: VFC = () => {

  const { treeReducer, dispatch } = useContext(GlobalContext)
  const { user } = useContext(AuthContext)
  const { selectedTreeNode } = treeReducer
  const ref = useRef(selectedTreeNode)
  const { createBanner, apiBannerHandler } = useMessage()
  const email = user?.email

  const [loading, setLoading] = useState(false)
  const [federations, setFederations] = useState<{ items?: Array<any> }>({})
  const [userFederation, setUserFederation] = useState({})
  const [txtError, setTxtError] = useState(false)

  const apiErrorHandler = useCallback(error => {
    apiBannerHandler(error)
    return []
  }, [apiBannerHandler])

  const fetchData = useCallback(async ({ tsg_id, loading = true, enableSuccessBanner }) => {
    loading && setLoading(true)

    try {
      const data = await LoadFederations(tsg_id)

      if (ref.current.id === tsg_id) {
        setFederations(data)
        setUserFederation(data?.items[0]?.owners.filter((o: { user: string }) => o.user === email))
      }

      if (federationMappingsEnabled) {
        const federationMappings: any[] = await Promise.all(data.items.reduce((accumulator: any, federation: any) => {
          const user_entry = federation?.owners?.find((owner: any) => owner?.user.toUpperCase() === email.toUpperCase())
          if (([IDP_OWNER_STATUS.VERIFIED, IDP_OWNER_STATUS.WARNING] as any[]).includes(user_entry?.verification?.status)) {
            accumulator.push(IdentityService.federations.federationMapping.list(selectedTreeNode?.id, federation?.domain).then(result => result.data.items))
          }
          return accumulator
        }, [])).catch(e => {
          return []
        })
        dispatch(setFederationMappings(_.flattenDeep(federationMappings)))
      }

      enableSuccessBanner && createBanner({ message: SUCCESS_GENERIC, bannerProps: { appearance: 'success' } })
    }
    catch (error) {
      loading && setLoading(false)
      return apiErrorHandler(error)
    }
    loading && setLoading(false)
  }, [createBanner, email, apiErrorHandler, selectedTreeNode?.id, dispatch])

  useEffect(() => {
    const tsg_id = selectedTreeNode?.id
    if (tsg_id !== ref.current?.id) {
      setFederations({})
    }
    ref.current = selectedTreeNode
    if (tsg_id) {
      fetchData({ tsg_id })
    }
    if (txtError) {
      createBanner({ message: LABEL_DELETE_OWNER_DNS_TEXT, bannerProps: { appearance: 'warning', type: 'page' } })
    }
  }, [fetchData, selectedTreeNode, setLoading, createBanner, txtError, dispatch])

  const addNewIdentityFederation: { openModal: Function } = useModal(DomainModal)

  const onAddFederationsClick = useCallback(() => {
    addNewIdentityFederation.openModal({ email, fetchData, federations })
  }, [email, fetchData, addNewIdentityFederation, federations])

  const cards = useMemo(() => federations?.items?.map((federation, index) => (
    <ProviderCard
      setTxtError={setTxtError}
      federations={federations}
      userFederation={userFederation}
      fetchData={fetchData}
      loading={loading}
      federation={federation}
      key={index}
    />
  )), [userFederation, loading, federations, fetchData])

  return (
    <ZIndex>
      <div className='tw-p-3 tw-mt-2'>
        <div className='tw-flex tw-justify-between'>
          <Title addClassName='tw-font-semibold' level={2} size='sm'>{LABEL_PROVIDERS_TITLE}</Title>
          {ARRAY.hasArray(federations?.items) && <div className='tw-flex tw-gap-4'>
            <Button appearance='primary' onClick={onAddFederationsClick}>{BUTTON_ADD_IDENTITY_FEDERATION}</Button>
          </div>}
        </div>
        <div className='tw-flex tw-flex-col tw-gap-4 '>
          {loading && <ScreenLoader/>}
          {ARRAY.hasArray(federations?.items) ? (
            <>
              <div className='tw-flex tw-flex-col tw-gap-4 tw-mt-4 tw-overflow-x-hidden tw-overflow-y-hidden'>
                {cards}
              </div>
            </>
          ) : (
            <NoDataScreen
              actions={!loading && (
                <>
                  <Button
                    appearance='primary'
                    onClick={onAddFederationsClick}
                  >
                    {BUTTON_ADD_IDENTITY_FEDERATION}
                  </Button>
                </>
              )}
              heading={loading ? 'Loading...' : HEADING_HAS_NO_FEDERATIONS}
              description={loading ? '' : DESC_HAS_NO_FEDERATIONS}
            />
          )}
        </div>
      </div>
    </ZIndex>
  )
}

export default Providers
